import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccordionService } from '../../services';
import { AccordionWidgetItem } from '../../../common/models';
import { PaddingPosition } from '../../../common/directives';
import { AccordionOpenStateEnum } from '../../../common/models/enums/accordion-open-state.enum';

@Component({
    selector: 'lib-accordion-widget',
    templateUrl: './accordion-widget.component.html',
    styleUrls: ['./accordion-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccordionWidgetComponent implements OnInit, OnDestroy {

  @ViewChildren(MatExpansionPanel, { read: ElementRef }) public panelsElements: QueryList<ElementRef>;
  @ViewChildren(MatExpansionPanel) public expansionPanels: QueryList<MatExpansionPanel>;

  @Input() public accordion: AccordionWidgetItem[];
  @Input() public spacing: PaddingPosition;
  @Input() public showEditButton = false;
  @Input() public scrollOnOpen = true;
  @Input() public showError = false;
  @Input() public shadow = true;
  @Input() public border = true;
  @Input() public translation = false;
  @Input() public openState: AccordionOpenStateEnum
  @Input() public animationOptions: { enabled: boolean, animation: any, delay: number } = {
    enabled: true,
    animation: '',
    delay: 0
  };

  @Output() public edit: EventEmitter<number> = new EventEmitter();
  @Output() public remove: EventEmitter<number> = new EventEmitter();
  @Output() public interactionCompleted: EventEmitter<number> = new EventEmitter();
  @Output() public orderChanged: EventEmitter<{previousIndex: number, currentIndex: number}> = new EventEmitter();

  public accordionOpenState = AccordionOpenStateEnum;

  private readonly completedAccordionIds: Set<string> = new Set();
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly accordionService: AccordionService
  ) { }

  ngOnInit() {
    this.processAccordionOpenState(this.openState);
    this.accordionService.onExpand.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe((id: string) => {
      const index = this.accordion?.findIndex(item => item.id === id);
      if (index === -1 || this.expansionPanels.length <= index) {
        return;
      }

      this.expansionPanels.get(index).open();
    });
  }

  public accordionOpened(index: number): void {
    this.updateCompletedState(index);

    if (this.scrollOnOpen) {
      this.scrollToTop(index);
    }
  }

  public scrollToTop(i: number): void {
    const element = this.panelsElements.toArray()[i];
    element?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  public emitEdit(event: any, index: number): void {
    event.stopPropagation();
    this.edit.emit(index);
  }

  public emitRemove(event: any, index: number): void {
    event.stopPropagation();
    this.remove.emit(index);
  }

  public onDrop(event: CdkDragDrop<AccordionWidgetItem[]>): void {
    this.orderChanged.emit({ previousIndex: event.previousIndex, currentIndex: event.currentIndex});
  }

  private processAccordionOpenState(state: AccordionOpenStateEnum): void {
    switch (state) {
      case AccordionOpenStateEnum.FIRST_OPEN:
        this.updateCompletedState(0);
        break;
      case AccordionOpenStateEnum.OPEN:
        this.completeAll();
        break;
    }
  }

  private updateCompletedState(index: number): void {
    const item = this.accordion?.[index];
    if (item) {
      this.completedAccordionIds.add(item.id);
    }

    this.interactionCompleted.emit(this.completedAccordionIds.size / this.accordion?.length);
  }

  private completeAll(): void {
    this.accordion.forEach(item => this.completedAccordionIds.add(item.id));
    this.interactionCompleted.emit(1);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
